import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { ChatConfiguration } from "../types";
import { ChatStreamingContext } from "../context/ChatStreamingContext";
import {
  hasTurnInFlight,
  StreamManagerState,
} from "../stores/StreamManagerStore";
import { StreamingState } from "../stores/StreamingStore";
import { ChatStreamSurface, useStreamBindings } from "./useStreamBindings";
import { mergeServerDialogue } from "../services/chatHelpers";
import { useDeepCompareEffect } from "src/reusable/hooks/useDeepCompareEffect";
import { isEqual } from "lodash";
import { StoreApi } from "zustand";

export interface OwnedChatStream extends ChatStreamSurface {
  /**
   * Swap to a fresh store for a brand-new chat. The dialogueId and
   * initialDialogue in effect at call time are ignored until the host moves
   * each to any other value, so the host may clear them later; it must move
   * them before it can reopen the same dialogue.
   */
  newChat: () => void;
  /** The owned store — hand it to ChatStreamProvider for descendants. */
  store: StoreApi<StreamingState>;
}

// Mounted owners per store. Two owners on one store both run config sync
// and clobber each other — the fault class this layer exists to end.
const ownerCount = new WeakMap<StoreApi<StreamingState>, number>();

/**
 * The last owner left `store`. Release it once no turn is in flight: close
 * the idle session (multi-turn sessions stay open between turns) and drop
 * the registry entry. While a turn streams or waits on a tool card the store
 * stays registered so a returning owner reattaches; if one has mounted again
 * by the time the turn ends, the release is theirs.
 */
const releaseWhenIdle = (
  manager: StoreApi<StreamManagerState>,
  store: StoreApi<StreamingState>,
) => {
  const release = () => {
    if ((ownerCount.get(store) ?? 0) > 0) return;
    store.getState().closeIdleSession();
    manager.getState().unregister(store);
  };
  if (!hasTurnInFlight(store)) {
    release();
    return;
  }
  const unsubscribe = store.subscribe(() => {
    if (hasTurnInFlight(store)) return;
    unsubscribe();
    release();
  });
};

/**
 * Owner hook: the host resolves (or creates) its streaming store and provides
 * it via ChatStreamProvider. Stream config is written by this hook's sync
 * effect and by the metadata event handler, which stashes the server-assigned
 * dialogueId on a fresh chat; descendants get a handle without
 * setStreamConfig. When the last owner leaves a store, its registry entry
 * is released and any idle session closed, once no turn is in flight.
 * A changed `initialDialogue` replaces the store's dialogue, unless
 * `mergeSeed` asks to keep the local messages it has not saved yet.
 */
export const useOwnChatStream = (
  config: ChatConfiguration = {},
  initialDialogue: any[] = [],
  { mergeSeed = false }: { mergeSeed?: boolean } = {},
): OwnedChatStream => {
  const manager = useContext(ChatStreamingContext);
  if (!manager) {
    throw new Error(
      "useOwnChatStream must be used within ChatStreamingProvider",
    );
  }

  const configRef = useRef(config);
  configRef.current = config;
  const initialDialogueRef = useRef(initialDialogue);
  initialDialogueRef.current = initialDialogue;

  // Render-phase resolution never writes the manager (a registry write
  // mid-render would update every manager subscriber while this component
  // renders); the effect below registers the store once it is committed.
  const resolveOrCreate = (cfg: ChatConfiguration) => {
    const m = manager.getState();
    return (
      (cfg.dialogueId != null && m.resolve(cfg.dialogueId)) || m.create(cfg)
    );
  };

  const [store, setStore] = useState(() => resolveOrCreate(config));

  // What newChat() left behind. While config still carries that dialogueId,
  // neither the render-phase adopt nor the config sync may bring it back;
  // while initialDialogue is still that history, the seed effect skips it.
  // Any other value lifts the respective guard.
  const ignoredDialogueIdRef = useRef<number | undefined>(undefined);
  const configDialogueId = config?.dialogueId;
  if (configDialogueId !== ignoredDialogueIdRef.current) {
    ignoredDialogueIdRef.current = undefined;
  }
  const dialogueIdIgnored = ignoredDialogueIdRef.current !== undefined;

  const ignoredInitialDialogueRef = useRef<any[] | undefined>(undefined);
  if (
    ignoredInitialDialogueRef.current !== undefined &&
    !isEqual(initialDialogue, ignoredInitialDialogueRef.current)
  ) {
    ignoredInitialDialogueRef.current = undefined;
  }
  const initialDialogueIgnored =
    ignoredInitialDialogueRef.current !== undefined;

  // Dialogue switch: adopt synchronously so effects only see the new store.
  // A fresh chat's id catching up via the metadata event is not a switch.
  if (
    configDialogueId != null &&
    !dialogueIdIgnored &&
    store.getState().streamConfig?.dialogueId !== configDialogueId
  ) {
    const next = resolveOrCreate(config);
    // The store is callable (zustand create), so never pass it bare to
    // setStore — React would run it as an updater.
    if (next !== store) setStore(() => next);
  }

  // Config sync — the owner is the only writer of stream config. While the
  // guard holds, the key is omitted (not nulled) so a metadata-assigned id
  // on the fresh store survives the merge.
  useDeepCompareEffect(() => {
    if (!config) return;
    if (dialogueIdIgnored) {
      const { dialogueId: _stale, ...rest } = config;
      store.getState().setStreamConfig(rest);
    } else {
      store.getState().setStreamConfig(config);
    }
  }, [config, store, dialogueIdIgnored]);

  // Each changed snapshot re-seeds the dialogue, but the history newChat() left behind is not a seed.
  useDeepCompareEffect(() => {
    if (initialDialogueIgnored) return;
    if (initialDialogue.length > 0) {
      store
        .getState()
        .setDialogue(
          mergeSeed
            ? (current) => mergeServerDialogue(initialDialogue, current)
            : initialDialogue,
        );
    }
  }, [initialDialogue, store, initialDialogueIgnored, mergeSeed]);

  // Register under the store's dialogueId once committed, and again the
  // moment the metadata event assigns one, so consumers can resolve a
  // brand-new chat's store.
  useEffect(() => {
    const register = (id: number | undefined) => {
      if (id != null) manager.getState().register(id, store);
    };
    let last = store.getState().streamConfig?.dialogueId;
    register(last);
    return store.subscribe((s) => {
      const id = s.streamConfig?.dialogueId;
      if (id !== last) {
        last = id;
        register(id);
      }
    });
  }, [manager, store]);

  // Counted here, not in the registry: only the hook sees mounts. Leaving
  // the store (swap, newChat, unmount) as its last owner releases it.
  useEffect(() => {
    const n = (ownerCount.get(store) ?? 0) + 1;
    ownerCount.set(store, n);
    if (n > 1 && import.meta.env.DEV) {
      console.warn(
        `[useOwnChatStream] ${n} owners on one store (dialogueId ` +
          `${store.getState().streamConfig?.dialogueId ?? "unset"}); ` +
          "each will sync its own config onto it",
      );
    }
    return () => {
      const remaining = (ownerCount.get(store) ?? 1) - 1;
      ownerCount.set(store, remaining);
      if (remaining === 0) releaseWhenIdle(manager, store);
    };
  }, [manager, store]);

  const newChat = useCallback(() => {
    ignoredDialogueIdRef.current = configRef.current.dialogueId;
    const history = initialDialogueRef.current;
    ignoredInitialDialogueRef.current =
      history.length > 0 ? history : undefined;
    const fresh = manager
      .getState()
      .create({ ...configRef.current, dialogueId: undefined });
    setStore(() => fresh);
  }, [manager]);

  const bindings = useStreamBindings(store, { restoreSubAgents: true });

  return { ...bindings, newChat, store };
};
